"use client";

// AccessLayers — a layered diagram of the authorization model:
//   ADMIN       (widest band) — every house, every account, every setting
//   OWNER       — their houses: details, photos, price, availability, bookings, bills, staff
//   HOUSE STAFF — one assigned house: approve bookings, manage bills
//   USER        — own reservations + bills assigned to them
// The bands narrow downwards: less access = narrower layer.
// Theme-aware via the app's CSS variables; static by design.

import { useI18n } from "@/lib/i18n/provider";

type Band = {
  y: number;
  w: number;
  chip: string;
  chipFill: string;
  chipText: string;
  outlined?: boolean;
  scope: string;
  pills: [string, number][];
};

export function AccessLayers() {
  const { t } = useI18n();

  const bands: Band[] = [
    {
      y: 42,
      w: 600,
      chip: "ADMIN",
      chipFill: "var(--foreground)",
      chipText: "var(--background)",
      scope: t("layers.adminScope"),
      pills: [
        [t("layers.chHouses"), 92],
        [t("layers.chBookings"), 104],
        [t("layers.chBills"), 78],
        [t("layers.chUsers"), 82],
        [t("layers.chSettings"), 96],
      ],
    },
    {
      y: 152,
      w: 516,
      chip: "OWNER",
      chipFill: "#7c3aed",
      chipText: "#ffffff",
      scope: t("layers.ownerScope"),
      pills: [
        [t("layers.chHouses"), 92],
        [t("layers.chBookings"), 104],
        [t("layers.chBills"), 78],
        [t("layers.chStaff"), 82],
      ],
    },
    {
      y: 262,
      w: 424,
      chip: "HOUSE STAFF",
      chipFill: "#0369a1",
      chipText: "#ffffff",
      scope: t("layers.staffScope"),
      pills: [
        [t("layers.chBookings"), 104],
        [t("layers.chBills"), 78],
      ],
    },
    {
      y: 372,
      w: 336,
      chip: "USER",
      chipFill: "var(--muted)",
      chipText: "currentColor",
      outlined: true,
      scope: t("layers.userScope"),
      pills: [
        [t("layers.chMyTrips"), 96],
        [t("layers.chAssignedBills"), 138],
      ],
    },
  ];

  return (
    <div>
      <svg
        viewBox="0 0 640 500"
        role="img"
        aria-label={t("layers.title")}
        className="mx-auto block w-full"
      >
        {/* Scope axis on the left */}
        <text x="26" y="52" fontSize="11" style={{ fill: "var(--muted)" }}>
          {t("layers.axisTop")}
        </text>
        <line x1="10" y1="64" x2="10" y2="424" stroke="var(--border)" strokeWidth="1.6" />
        <path d="M5 420 L10 430 L15 420" fill="none" stroke="var(--border)" strokeWidth="1.6" />
        <text x="26" y="438" fontSize="11" style={{ fill: "var(--muted)" }}>
          {t("layers.axisBottom")}
        </text>

        {bands.map((b, bi) => {
          const x = (640 - b.w) / 2;
          const chipW = b.chip.length * 8.6 + 26;
          const isUser = bi === 3;
          return (
            <g key={bi}>
              <rect
                x={x}
                y={b.y}
                width={b.w}
                height={96}
                rx={14}
                style={{ fill: isUser ? "var(--muted)" : b.chipFill, fillOpacity: isUser ? 0.3 : 1 }}
                stroke={isUser ? "var(--border)" : "none"}
                strokeWidth={isUser ? 1.4 : 0}
              />
              {/* Role chip */}
              <rect
                x={x + 18}
                y={b.y + 14}
                width={chipW}
                height={26}
                rx={13}
                style={{ fill: b.chipFill }}
                stroke={isUser ? "var(--border)" : "none"}
                strokeWidth={isUser ? 1.2 : 0}
              />
              <text
                x={x + 18 + chipW / 2}
                y={b.y + 31}
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                style={{ fill: b.chipText }}
              >
                {b.chip}
              </text>
              {/* Scope description next to the chip */}
              <text
                x={x + 18 + chipW + 14}
                y={b.y + 31}
                fontSize="11.5"
                style={{
                  fill: isUser ? "currentColor" : "#ffffff",
                  opacity: isUser ? 0.8 : 0.94,
                }}
              >
                {b.scope}
              </text>
              {/* Access pills */}
              {b.pills.map(([label, w], pi) => {
                const px =
                  x +
                  18 +
                  b.pills.slice(0, pi).reduce((sum, [, pw]) => sum + pw + 10, 0);
                return (
                  <g key={label}>
                    <rect
                      x={px}
                      y={b.y + 54}
                      width={w}
                      height={24}
                      rx={12}
                      style={{ fill: "var(--card)" }}
                      stroke="var(--border)"
                      strokeWidth="1.2"
                    />
                    <text
                      x={px + w / 2}
                      y={b.y + 70}
                      textAnchor="middle"
                      fontSize="11.5"
                      fontWeight="600"
                      fill="currentColor"
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {[
          { chip: "ADMIN", chipCls: "bg-foreground text-background", text: t("layers.legendAdmin") },
          { chip: "OWNER", chipCls: "bg-violet-700 text-white", text: t("layers.legendOwner") },
          { chip: "HOUSE STAFF", chipCls: "bg-sky-700 text-white", text: t("layers.legendStaff") },
          { chip: "USER", chipCls: "border bg-card text-foreground", text: t("layers.legendUser") },
        ].map((row) => (
          <div key={row.chip} className="flex items-center gap-2 text-xs">
            <span
              className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${row.chipCls}`}
            >
              {row.chip}
            </span>
            <span className="text-muted-foreground">{row.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
